import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";

// Formik + Yup
import * as Yup from "yup";
import { useFormik } from "formik";

// Actions
import { socialLogin } from "../../store/actions";

// Assets
import logoImg from "assets/images/logo.png";

// Helpers
import { FlagContext } from "libs/context/FlagContext";
import { useUserContext } from "libs/context/UserContext";
import { formatDataBeforeSend } from "helpers/GeneralHelper";

const Login = (props) => {
  const [ipData, setIpData] = useState(null);
  const [showPassword, setShowPassword] = useState(false); // 👈 Added
  const { updateLang } = useContext(FlagContext);
  const formatOptions = [];

  document.title = "Login | Ancile Services";
  let navigate = useNavigate();

  const dispatch = useDispatch();
  const { loginUser, loading } = useUserContext() || {};

  // Redux selector
  const LoginProperties = createSelector(
    (state) => state.Login,
    (login) => ({
      error: login.error,
    })
  );
  const { error } = useSelector(LoginProperties);

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const inputFields = {};
      const formData =
        formatDataBeforeSend(inputFields, formatOptions);

      formData.append("userEmail", values.email);
      formData.append("password", values.password);
      loginUser(formData);
    },
  });

  const signIn = (type) => {
    dispatch(socialLogin(type, props.router.navigate));
  };

  return (
    <React.Fragment>
      <div className="bg-cream d-flex align-items-center justify-content-center">
        <Container>
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              {/* Logo */}
              <div className="text-center mb-4">
                <img src={logoImg} alt="logo" height="45" />
              </div>

              {/* Card */}
              <Card className="login-card">
                <CardBody className="p-4">
                  <h4 className="text-center mb-4 fw-bold">Sign in</h4>

                  <Form onSubmit={formik.handleSubmit}>
                    {error ? (
                      <Alert color="danger">{error}</Alert>
                    ) : null}

                    {/* Email */}
                    <div className="mb-3">
                      <Input
                        name="email"
                        type="email"
                        placeholder="Enter Email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        invalid={
                          formik.touched.email &&
                          Boolean(formik.errors.email)
                        }
                        className="h-45"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <FormFeedback>
                          {formik.errors.email}
                        </FormFeedback>
                      )}
                    </div>

                    {/* Password */}
                    <div className="mb-3 position-relative">
                      <Input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        invalid={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        className="h-45 pe-5"
                      />

                      {/* Eye Toggle */}
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                       className={`eye-show-hide ${formik.touched.password &&
                        formik.errors.password? 'eye-show-hide-pos-error' : 'eye-show-hide-pos'}`}
                      >
                        <i className={`mdi ${showPassword ? "mdi-eye-off" : "mdi-eye"}`} />

                      </span>

                      {formik.touched.password &&
                        formik.errors.password && (
                          <FormFeedback>
                            {formik.errors.password}
                          </FormFeedback>
                        )}
                    </div>

                    {/* Button */}
                    <div className="d-grid mt-3">
                      <button
                        type="submit"
                        disabled={formik.isSubmitting || loading}
                        className="login-continue-button"
                      >
                        Sign In
                        {loading && (
                          <Spinner
                            size="sm"
                            className="me-4"
                          />
                        )}
                      </button>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              {/* Footer */}
              <div className="text-center mt-4">
                <small className="power-ancile ">
                  ©{new Date().getFullYear()} Powered by Ancile Services
                </small>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default withRouter(Login);
